<script setup lang="ts">
  import { reactive, ref, useTemplateRef, watch } from 'vue'
  import type {
    StakeholderContact,
    StakeholderType,
    StakeholderContactType,
    AiExtractDataContactResponse
  } from '@gat-ship/types'
  import {
    isStakeholderContactPerson,
    isStakeholderContactOther
  } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    Label,
    FormField,
    InputGroupInput,
    isValidPhoneNumber,
    InputGroupPhone,
    Button,
    Modal,
    ModalFooter,
    ModalHeader,
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ModalContent,
    ModalHeaderTitle,
    useAiFormFill,
    useForm,
    useFormSubmit,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { IconEdit, IconUser, IconOrganization } from '@gat-ship/icons'
  import { ToggleGroup, ToggleGroupItem } from '@gat-ship/vue-components'
  import { SmartPasteButton } from '@gat-ship/smart-paste'
  import { globalUtils, useFormatting } from '@gat-ship/utils'
  import {
    useContactProvider,
    type ContactModalParams
  } from '../../../composables/useStakeholderContacts'

  const { formatPhoneNumber } = useFormatting()
  const modalRef = useTemplateRef('contactModal')
  const isOpen = defineModel<boolean>('open', { default: false })
  const contactsProvider = useContactProvider()

  /** Current action type — drives title, button text, and view vs edit mode. */
  const mainActionType = ref<'add' | 'update' | 'view'>('add')

  /** Toggle between person and other contact type. */
  const contactType = ref<StakeholderContactType>('person')

  /** Stashed metadata that isn't part of the form but needed for API calls. */
  const contactMeta = ref({
    id: '',
    stakeholderId: '',
    stakeholderName: '',
    stakeholderShortName: '',
    stakeholderType: 'client' as StakeholderType
  })

  /** Stashed read-only contact for the view mode. */
  const viewContact = ref<StakeholderContact>()

  // --- Zod schema ---
  // All fields present; type-specific ones are optional so the schema
  // validates regardless of which contact type is active.
  const contactSchema = z
    .object({
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      otherName: z.string().optional(),
      email: z.string().optional(),
      mobilePhone: z.string().optional(),
      directLine: z.string().optional(),
      jobTitle: z.string().optional(),
      department: z.string().optional()
    })
    .superRefine((data, ctx) => {
      if (contactType.value === 'person') {
        if (!data.firstName || data.firstName.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'First name is required',
            path: ['firstName']
          })
        }
        if (!data.lastName || data.lastName.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Last name is required',
            path: ['lastName']
          })
        }
      } else {
        if (!data.otherName || data.otherName.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Name is required',
            path: ['otherName']
          })
        }
      }
    })

  type ContactFormValues = z.infer<typeof contactSchema>

  /** Optional text inputs persist as `null` when left empty. */
  const orNull = (value: string | undefined) => value || null

  function buildContactPayload(value: ContactFormValues): StakeholderContact {
    const meta = contactMeta.value
    const base = {
      id: meta.id,
      stakeholderId: meta.stakeholderId,
      stakeholderName: meta.stakeholderName,
      stakeholderShortName: meta.stakeholderShortName,
      stakeholderType: meta.stakeholderType,
      isInactive: false,
      email: orNull(value.email),
      mobilePhone: orNull(value.mobilePhone),
      directLine: orNull(value.directLine)
    }
    if (contactType.value === 'person') {
      return {
        ...base,
        contactType: 'person',
        firstName: orNull(value.firstName),
        lastName: orNull(value.lastName),
        jobTitle: orNull(value.jobTitle),
        department: orNull(value.department)
      }
    }
    return {
      ...base,
      contactType: 'other',
      otherName: orNull(value.otherName)
    }
  }

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      otherName: '',
      email: '',
      mobilePhone: '',
      directLine: '',
      jobTitle: '',
      department: ''
    } as ContactFormValues,
    validators: {
      onChange: contactSchema
    },
    onSubmit: async ({ value }) => {
      modalRef.value?.closeModal()
      const contactPayload = buildContactPayload(value)
      if (mainActionType.value === 'add') {
        await contactsProvider.commands.addContact(contactPayload)
      } else if (mainActionType.value === 'update') {
        await contactsProvider.commands.updateContact(contactPayload)
      }
    }
  })

  // Phone-format validation parity with the old FormPhoneField (empty
  // allowed, non-empty must be a valid phone number).
  const phoneValidators = {
    onBlur: ({ value }: { value: string }) =>
      !value || value.length === 0
        ? undefined
        : isValidPhoneNumber(value)
          ? undefined
          : 'Invalid phone number'
  }

  // --- AI form fill ---
  const ai = useAiFormFill<AiExtractDataContactResponse>(form)

  function onExtracted(rawData: Record<string, unknown>) {
    const data = rawData as AiExtractDataContactResponse
    if (data.contactType?.value) {
      contactType.value = data.contactType.value
    }
    ai.applyResponse(data)
  }

  // --- Submit state ---
  // `requireDirty: false` — the add-flow can submit when values still
  // match defaults (single-required-field case), and the edit-flow
  // re-seeds defaults via `form.reset()` so values == defaults after open.
  const { submit, errorMessage } = useFormSubmit(form, {
    requireDirty: false
  })

  watch(errorMessage, (msg) => {
    if (msg) {
      isOpen.value = true
    }
  })

  const okButtonProps = reactive({
    text: 'Add Contact',
    handleClick: submit
  })

  function getModalTitle() {
    switch (mainActionType.value) {
      case 'view':
        if (!viewContact.value) return 'Contact'
        return isStakeholderContactPerson(viewContact.value)
          ? `${viewContact.value.firstName} ${viewContact.value.lastName}`
          : (viewContact.value.otherName ?? 'Contact')
      case 'add':
        return 'Add Contact'
      case 'update':
        return 'Update Contact'
    }
  }

  const modalTitle = ref('')

  /**
   * Read a contact-type-specific field off either union variant:
   * missing on this variant, or null, reads as ''.
   */
  function contactFieldOrEmpty(
    contact: StakeholderContact | undefined,
    field: string
  ): string {
    if (!contact) return ''
    const value = (contact as unknown as Record<string, unknown>)[field]
    return typeof value === 'string' ? value : ''
  }

  /** Seed the form from an existing contact (edit) or blanks (add). */
  function seedFormFromContact(contact: StakeholderContact | undefined) {
    form.reset({
      firstName: contactFieldOrEmpty(contact, 'firstName'),
      lastName: contactFieldOrEmpty(contact, 'lastName'),
      otherName: contactFieldOrEmpty(contact, 'otherName'),
      email: contact?.email ?? '',
      mobilePhone: contact?.mobilePhone ?? '',
      directLine: contact?.directLine ?? '',
      jobTitle: contactFieldOrEmpty(contact, 'jobTitle'),
      department: contactFieldOrEmpty(contact, 'department')
    })
  }

  function showModal(params: ContactModalParams) {
    mainActionType.value = params.mainActionType
    contactType.value = params.contactType

    contactMeta.value = {
      id: params.contact?.id ?? globalUtils.functions.uuid(),
      stakeholderId: params.stakeholderId,
      stakeholderName: params.stakeholderName,
      stakeholderShortName: params.stakeholderShortName,
      stakeholderType: params.stakeholderType
    }

    viewContact.value = params.contact

    if (params.mainActionType === 'view') {
      // View mode: no form, just read-only display. Use custom footer via slot.
      modalTitle.value = getModalTitle()
    } else {
      const isAdd = params.mainActionType === 'add'
      okButtonProps.text = isAdd ? 'Add Contact' : 'Save'
      modalTitle.value = isAdd ? 'Add Contact' : 'Update Contact'
      seedFormFromContact(params.contact)
    }

    isOpen.value = true
  }

  /** Switch from view mode into update mode. */
  function switchToEditMode() {
    if (!viewContact.value) return
    showModal({
      mainActionType: 'update',
      stakeholderId: contactMeta.value.stakeholderId,
      stakeholderName: contactMeta.value.stakeholderName,
      stakeholderShortName: contactMeta.value.stakeholderShortName,
      stakeholderType: contactMeta.value.stakeholderType,
      contactType: contactType.value,
      contact: viewContact.value
    })
  }

  defineExpose({ showModal })
</script>

<template>
  <modal ref="contactModal" v-model:open="isOpen" class="min-w-[400px]">
    <ModalHeader>
      <div class="flex w-full flex-col gap-2">
        <ModalHeaderTitle>
          {{ getModalTitle() }}
          <div class="ml-auto flex items-center gap-1">
            <SmartPasteButton
              v-if="mainActionType !== 'view'"
              prompt-type="data-extraction"
              target="DE:Stakeholders.V1.CreateContact"
              label="Smart Paste"
              @extracted="onExtracted"
            />
          </div>
        </ModalHeaderTitle>
        <ToggleGroup
          v-if="mainActionType === 'add'"
          class="w-full"
          type="single"
          size="sm"
          variant="outline"
          :model-value="contactType"
          required
          @update:model-value="
            (val) => {
              if (val) contactType = val as StakeholderContactType
            }
          "
        >
          <ToggleGroupItem
            value="person"
            aria-label="Toggle Chart Pie"
            class="flex w-1/2 items-center gap-1"
          >
            <IconUser class="size-4" />
            Contact Person
          </ToggleGroupItem>
          <ToggleGroupItem
            value="other"
            aria-label="Toggle Contact Point"
            class="flex w-1/2 items-center gap-1"
          >
            <IconOrganization class="size-4" />
            Contact Point
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
    </ModalHeader>
    <ModalContent class="flex flex-col gap-5">
      <!-- View mode: read-only display -->
      <template v-if="mainActionType === 'view' && viewContact">
        <template v-if="isStakeholderContactPerson(viewContact)">
          <ModalSection>
            <ModalSectionHeader>
              <ModalSectionHeaderTitle
                >Personal Information</ModalSectionHeaderTitle
              >
            </ModalSectionHeader>
            <div>
              <Label
                class="text-xs font-medium tracking-wider text-neutral-700 dark:text-neutral-200"
                >First name</Label
              >
              <div class="mt-1 text-sm font-medium text-neutral-950">
                {{ viewContact.firstName ?? '-' }}
              </div>
            </div>
            <div>
              <Label
                class="text-xs font-medium tracking-wider text-neutral-700 dark:text-neutral-200"
                >Last name</Label
              >
              <div class="mt-1 text-sm font-medium text-neutral-950">
                {{ viewContact.lastName ?? '-' }}
              </div>
            </div>
          </ModalSection>
        </template>
        <template v-else-if="isStakeholderContactOther(viewContact)">
          <ModalSection>
            <ModalSectionHeader>
              <ModalSectionHeaderTitle
                >Personal Information</ModalSectionHeaderTitle
              >
            </ModalSectionHeader>
            <div>
              <Label
                class="text-xs font-medium tracking-wider text-neutral-700 dark:text-neutral-200"
                >Name</Label
              >
              <div class="mt-1 text-sm font-medium text-neutral-950">
                {{ viewContact.otherName ?? '-' }}
              </div>
            </div>
          </ModalSection>
        </template>

        <ModalSection>
          <ModalSectionHeader>
            <ModalSectionHeaderTitle
              >Contact Information</ModalSectionHeaderTitle
            >
          </ModalSectionHeader>
          <div>
            <Label
              class="text-xs font-medium tracking-wider text-neutral-700 dark:text-neutral-200"
              >Email</Label
            >
            <div class="mt-1 text-sm font-medium text-neutral-950">
              <a
                v-if="viewContact.email"
                :href="`mailto:${viewContact.email}`"
                class="hover:text-link/80"
                @click.stop
                >{{ viewContact.email }}</a
              >
              <template v-else>-</template>
            </div>
          </div>
          <div>
            <Label
              class="text-xs font-medium tracking-wider text-neutral-700 dark:text-neutral-200"
              >Mobile</Label
            >
            <div class="mt-1 text-sm font-medium text-neutral-950">
              <a
                v-if="viewContact.mobilePhone"
                :href="`tel:${formatPhoneNumber(viewContact.mobilePhone)}`"
                class="hover:text-link/80"
                @click.stop
                >{{ formatPhoneNumber(viewContact.mobilePhone) }}</a
              >
              <template v-else>-</template>
            </div>
          </div>
          <div>
            <Label
              class="text-xs font-medium tracking-wider text-neutral-700 dark:text-neutral-200"
              >Phone</Label
            >
            <div class="mt-1 text-sm font-medium text-neutral-950">
              <a
                v-if="viewContact.directLine"
                :href="`tel:${formatPhoneNumber(viewContact.directLine)}`"
                class="hover:text-link/80"
                @click.stop
                >{{ formatPhoneNumber(viewContact.directLine) }}</a
              >
              <template v-else>-</template>
            </div>
          </div>
        </ModalSection>

        <template v-if="isStakeholderContactPerson(viewContact)">
          <ModalSection>
            <ModalSectionHeader>
              <ModalSectionHeaderTitle
                >Business Related Information</ModalSectionHeaderTitle
              >
            </ModalSectionHeader>
            <div>
              <Label
                class="text-xs font-medium tracking-wider text-neutral-700 dark:text-neutral-200"
                >Job Title</Label
              >
              <div class="mt-1 text-sm font-medium text-neutral-950">
                {{ viewContact.jobTitle ?? '-' }}
              </div>
            </div>
            <div>
              <Label
                class="text-xs font-medium tracking-wider text-neutral-700 dark:text-neutral-200"
                >Department</Label
              >
              <div class="mt-1 text-sm font-medium text-neutral-950">
                {{ viewContact.department ?? '-' }}
              </div>
            </div>
          </ModalSection>
        </template>
      </template>

      <!-- Add / Update mode: form fields -->
      <template v-if="mainActionType !== 'view'">
        <ModalSection>
          <ModalSectionHeader>
            <ModalSectionHeaderTitle
              >Personal Information</ModalSectionHeaderTitle
            >
          </ModalSectionHeader>
          <FieldGroup v-if="contactType === 'person'">
            <FormField
              :form="form"
              name="firstName"
              label="First name"
              required
              ai
            >
              <template #default="{ field, aiLoading, handleChange }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  autofocus
                  :disabled="aiLoading"
                  :placeholder="aiLoading ? 'Generating…' : undefined"
                  :aria-busy="aiLoading || undefined"
                  :data-testid="
                    testIds.stakeholder.forms.contactPersonInfo.firstNameInput
                  "
                  @update:model-value="handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
            <FormField
              :form="form"
              name="lastName"
              label="Last name"
              required
              ai
            >
              <template #default="{ field, aiLoading, handleChange }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  :disabled="aiLoading"
                  :placeholder="aiLoading ? 'Generating…' : undefined"
                  :aria-busy="aiLoading || undefined"
                  :data-testid="
                    testIds.stakeholder.forms.contactPersonInfo.lastNameInput
                  "
                  @update:model-value="handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>
          <FieldGroup v-if="contactType === 'other'">
            <FormField :form="form" name="otherName" label="Name" required ai>
              <template #default="{ field, aiLoading, handleChange }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  autofocus
                  :disabled="aiLoading"
                  :placeholder="aiLoading ? 'Generating…' : undefined"
                  :aria-busy="aiLoading || undefined"
                  :data-testid="
                    testIds.stakeholder.forms.contactPersonInfo.otherNameInput
                  "
                  @update:model-value="handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>
        </ModalSection>

        <ModalSection>
          <ModalSectionHeader>
            <ModalSectionHeaderTitle
              >Contact Information</ModalSectionHeaderTitle
            >
          </ModalSectionHeader>
          <FieldGroup>
            <FormField :form="form" name="email" label="Email" ai>
              <template #default="{ field, aiLoading, handleChange }">
                <InputGroupInput
                  :id="field.name"
                  :model-value="field.state.value ?? ''"
                  :disabled="aiLoading"
                  :placeholder="aiLoading ? 'Generating…' : undefined"
                  :aria-busy="aiLoading || undefined"
                  :data-testid="
                    testIds.stakeholder.forms.contactPersonInfo.emailInput
                  "
                  @update:model-value="handleChange"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
            <FormField
              :form="form"
              name="mobilePhone"
              label="Mobile"
              ai
              :validators="phoneValidators"
            >
              <template #default="{ field, aiLoading, handleChange }">
                <InputGroupPhone
                  :id="field.name"
                  :model-value="field.state.value ?? undefined"
                  :disabled="aiLoading"
                  :placeholder="aiLoading ? 'Generating…' : undefined"
                  :data-testid="
                    testIds.stakeholder.forms.contactPersonInfo.mobilePhoneInput
                  "
                  @update:model-value="(v) => handleChange(v ?? '')"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
            <FormField
              :form="form"
              name="directLine"
              label="Phone"
              ai
              :validators="phoneValidators"
            >
              <template #default="{ field, aiLoading, handleChange }">
                <InputGroupPhone
                  :id="field.name"
                  :model-value="field.state.value ?? undefined"
                  :disabled="aiLoading"
                  :placeholder="aiLoading ? 'Generating…' : undefined"
                  :data-testid="
                    testIds.stakeholder.forms.contactPersonInfo.directLineInput
                  "
                  @update:model-value="(v) => handleChange(v ?? '')"
                  @blur="field.handleBlur"
                />
              </template>
            </FormField>
          </FieldGroup>
        </ModalSection>

        <template v-if="contactType === 'person'">
          <ModalSection>
            <ModalSectionHeader>
              <ModalSectionHeaderTitle
                >Business Related Information</ModalSectionHeaderTitle
              >
            </ModalSectionHeader>
            <FieldGroup>
              <FormField :form="form" name="jobTitle" label="Job Title" ai>
                <template #default="{ field, aiLoading, handleChange }">
                  <InputGroupInput
                    :id="field.name"
                    :model-value="field.state.value ?? ''"
                    :disabled="aiLoading"
                    :placeholder="aiLoading ? 'Generating…' : undefined"
                    :aria-busy="aiLoading || undefined"
                    :data-testid="
                      testIds.stakeholder.forms.contactPersonInfo.jobTitleInput
                    "
                    @update:model-value="handleChange"
                    @blur="field.handleBlur"
                  />
                </template>
              </FormField>
              <FormField :form="form" name="department" label="Department" ai>
                <template #default="{ field, aiLoading, handleChange }">
                  <InputGroupInput
                    :id="field.name"
                    :model-value="field.state.value ?? ''"
                    :disabled="aiLoading"
                    :placeholder="aiLoading ? 'Generating…' : undefined"
                    :aria-busy="aiLoading || undefined"
                    :data-testid="
                      testIds.stakeholder.forms.contactPersonInfo
                        .departmentInput
                    "
                    @update:model-value="handleChange"
                    @blur="field.handleBlur"
                  />
                </template>
              </FormField>
            </FieldGroup>
          </ModalSection>
        </template>
      </template>
    </ModalContent>
    <ModalFooter v-if="mainActionType === 'view'">
      <Button size="sm" variant="outline" @click="switchToEditMode">
        <IconEdit class="size-4" />Update Contact Details
      </Button>
      <Button variant="outline" @click="isOpen = false">Close</Button>
    </ModalFooter>
    <ModalFooter v-else>
      <Button @click="submit">
        {{ mainActionType === 'add' ? 'Add Contact' : 'Save' }}
      </Button>
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
